import { useState, useEffect } from 'react';
import { Sparkles, Mail, Phone, Zap, FileText, Clock, UserPlus, ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import { api } from './api';

const TYPE_ICON = {
  email:    Mail,
  call:     Phone,
  workflow: Zap,
  note:     FileText,
  wait:     Clock,
  qualify:  UserPlus,
};

const TYPE_COLOR = {
  email:    'text-[#338edc]',
  call:     'text-[#EA580C]',
  workflow: 'text-[#338edc]',
  note:     'text-[var(--text-secondary)]',
  wait:     'text-[var(--text-tertiary)]',
  qualify:  'text-[#0891B2]',
};

// #1083 Phase 4/E — AI-Vorschläge nächste Aktionen
export default function NextActions({ leadId, onAction }) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api(`/api/leads/${leadId}/next-actions`);
      setActions(data.actions || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  if (!loaded && loading) {
    return (
      <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-3 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin text-[#338edc]" />
        <span className="text-[12px] text-[var(--text-tertiary)]">AI analysiert nächste Aktionen…</span>
      </div>
    );
  }

  if (loaded && !loading && actions.length === 0 && !error) {
    return null;
  }

  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-[#338edc]" />
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
            Nächste Aktionen — AI-Vorschläge
          </h3>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="p-1 rounded hover:bg-[var(--bg-inset)] text-[var(--text-tertiary)] disabled:opacity-50"
          title="Neu generieren"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </button>
      </div>
      {error ? (
        <div className="text-[12px] text-red-600">Fehler: {error}</div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {actions.map((a, i) => {
            const Icon = TYPE_ICON[a.type] || FileText;
            const color = TYPE_COLOR[a.type] || 'text-[var(--text-secondary)]';
            return (
              <button
                key={i}
                onClick={() => onAction?.(a)}
                title={a.hint}
                className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--border-card)] bg-[var(--bg-inset)] hover:bg-[var(--bg-card)] hover:border-[#338edc] transition-all text-[12px] text-[var(--text-primary)]"
              >
                <Icon className={`w-3.5 h-3.5 ${color}`} />
                <span className="font-medium">{a.label}</span>
                <ChevronRight className="w-3 h-3 text-[var(--text-tertiary)] group-hover:text-[#338edc] transition-colors" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
